<template>
	<div>
		<v-dialog
			:value="true"
			max-width="1100"
			@click:outside="onClose"
			@keydown.esc="onClose"
		>
			<v-card>
				<v-card-title class="pa-0">
					<v-toolbar :color="`${isDark ? '' : 'grey lighten-3'}`" flat dense>
						<v-tooltip bottom>
							<template v-slot:activator="{ on }">
								<v-btn icon @click="onClose" v-on="on">
									<v-icon>mdi-close</v-icon>
								</v-btn>
							</template>
							<span>{{ $t('tooltip.close') }}</span>
						</v-tooltip>
						<v-toolbar-title>{{ original_file_name }}</v-toolbar-title>
						<v-spacer></v-spacer>

						<template v-if="isPdf && totalPages > 0">
							<v-btn
								icon
								small
								:disabled="currentPage <= 1 || rendering"
								@click="goToPage(currentPage - 1)"
							>
								<v-icon>mdi-chevron-left</v-icon>
							</v-btn>
							<span class="mx-2 text-body-2">
								{{ currentPage }} / {{ totalPages }}
							</span>
							<v-btn
								icon
								small
								:disabled="currentPage >= totalPages || rendering"
								@click="goToPage(currentPage + 1)"
							>
								<v-icon>mdi-chevron-right</v-icon>
							</v-btn>
							<v-divider vertical class="mx-2"></v-divider>
							<v-btn
								icon
								small
								:disabled="scale <= 0.5 || rendering"
								@click="zoomOut"
							>
								<v-icon>mdi-minus</v-icon>
							</v-btn>
							<v-btn
								x-small
								text
								:disabled="rendering || scale === 1.0"
								@click="resetZoom"
								class="mx-1"
								style="min-width: 50px"
							>
								{{ Math.round(scale * 100) }}%
							</v-btn>
							<v-btn
								icon
								small
								:disabled="scale >= 3 || rendering"
								@click="zoomIn"
							>
								<v-icon>mdi-plus</v-icon>
							</v-btn>
							<v-divider vertical class="mx-2"></v-divider>
						</template>

						<v-btn
							color="primary"
							class="ml-4"
							:loading="downloading"
							:disabled="!base64"
							@click="handleDownload"
						>
							<v-icon left>mdi-download</v-icon>
							{{ $t('button.download').toLocaleUpperCase('TR') }}
						</v-btn>
					</v-toolbar>
				</v-card-title>
				<v-divider></v-divider>

				<v-card-text style="padding: 0">
					<div
						v-if="loading"
						class="d-flex justify-center align-center"
						style="height: 600px; background: #525659"
					>
						<v-progress-circular
							indeterminate
							color="white"
							size="48"
						></v-progress-circular>
					</div>

					<div
						v-else-if="error"
						class="d-flex justify-center align-center flex-column"
						style="height: 600px"
					>
						<v-icon large color="error" class="mb-2">mdi-alert-circle</v-icon>
						<p class="text-body-1">{{ error }}</p>
						<v-btn color="primary" @click="handleDownload" v-if="base64">
							<v-icon left>download</v-icon>
							{{ $t('button.download') }}
						</v-btn>
					</div>

					<div
						v-else-if="isPdf"
						ref="pdfContainer"
						class="pdf-container"
						@wheel="handleWheel"
					>
						<canvas ref="pdfCanvas" class="pdf-canvas"></canvas>
					</div>

					<div
						v-else-if="isImage"
						class="d-flex justify-center align-center"
						style="height: 600px; overflow: auto; background: #f5f5f5"
					>
						<img
							:src="base64"
							:alt="original_file_name"
							style="max-width: 100%; max-height: 100%; object-fit: contain"
						/>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import api from '@/api';
import * as pdfjsLib from 'pdfjs-dist';
import PdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';

// Use bundled worker (avoids CSP issues with external scripts)
pdfjsLib.GlobalWorkerOptions.workerSrc = PdfjsWorker;

export default {
	name: 'ViewFile',
	props: {
		fileId: {
			type: Number,
			required: true,
		},
		onClose: {
			type: Function,
			required: true,
		},
	},
	computed: {
		...mapState({
			isDark: state => state.currentUser.isDark,
		}),
		isPdf() {
			return this.content_type === 'application/pdf';
		},
		isImage() {
			return ['image/jpeg', 'image/gif', 'image/png', 'image/webp'].includes(
				this.content_type,
			);
		},
	},
	data() {
		return {
			base64: null,
			original_file_name: null,
			content_type: null,
			loading: true,
			rendering: false,
			error: null,
			downloading: false,
			// PDF.js specific
			pdfDoc: null,
			currentPage: 1,
			totalPages: 0,
			scale: 1.0,
			pdfBytes: null,
		};
	},
	mounted() {
		this.handleGetFile({ fileId: this.fileId });
	},
	beforeDestroy() {
		if (this.pdfDoc) {
			this.pdfDoc.destroy();
			this.pdfDoc = null;
		}
	},
	methods: {
		async handleGetFile({ fileId }) {
			this.loading = true;
			this.error = null;

			try {
				const base64Response = await api.files.base64.get({ id: fileId });
				const { base64, original_file_name, content_type } =
					base64Response.data;

				this.original_file_name = original_file_name;
				this.content_type = content_type;
				this.base64 = `data:${content_type};base64,${base64}`;

				if (this.isPdf) {
					// Store bytes for PDF rendering
					const binaryString = atob(base64);
					const bytes = new Uint8Array(binaryString.length);
					for (let i = 0; i < binaryString.length; i++) {
						bytes[i] = binaryString.charCodeAt(i);
					}
					this.pdfBytes = bytes;

					// Load PDF document (loading will be set to false after first page renders)
					await this.loadPdfDocument();
				} else if (this.isImage) {
					this.loading = false;
				} else {
					// For other file types, trigger download
					this.loading = false;
					this.handleDownload();
					this.onClose();
				}
			} catch (err) {
				console.error('Error loading file:', err);
				this.error = this.$t('error.file-load-failed') || 'Failed to load file';
				this.loading = false;
			}
		},

		async loadPdfDocument() {
			try {
				const loadingTask = pdfjsLib.getDocument({
					data: this.pdfBytes,
					// Security: disable JavaScript execution in PDFs
					isEvalSupported: false,
				});

				this.pdfDoc = await loadingTask.promise;
				this.totalPages = this.pdfDoc.numPages;

				// Wait for DOM to update, then render first page
				// Keep loading=true until first page is rendered
				await this.$nextTick();
				await new Promise(resolve => setTimeout(resolve, 100));
				await this.renderPage(1, true);
			} catch (err) {
				console.error('Error loading PDF:', err);
				this.error =
					this.$t('error.pdf-render-failed') ||
					'Failed to render PDF. You can download the file instead.';
				this.loading = false;
			}
		},

		async renderPage(pageNum, isInitialLoad = false) {
			if (!this.pdfDoc) {
				return;
			}

			if (!isInitialLoad) {
				this.rendering = true;
			}

			try {
				const page = await this.pdfDoc.getPage(pageNum);

				// For initial load, now we can show the canvas container
				if (isInitialLoad) {
					this.loading = false;
					await this.$nextTick();
				}

				// Wait for canvas ref to be available
				let attempts = 0;
				while (!this.$refs.pdfCanvas && attempts < 10) {
					await this.$nextTick();
					await new Promise(resolve => setTimeout(resolve, 20));
					attempts++;
				}

				if (!this.$refs.pdfCanvas) {
					console.error('Canvas not available');
					return;
				}

				const canvas = this.$refs.pdfCanvas;
				const context = canvas.getContext('2d');

				// Get viewport at current scale
				const viewport = page.getViewport({ scale: this.scale });

				// Set canvas dimensions (use device pixel ratio for crisp rendering)
				const pixelRatio = window.devicePixelRatio || 1;
				canvas.width = viewport.width * pixelRatio;
				canvas.height = viewport.height * pixelRatio;
				canvas.style.width = viewport.width + 'px';
				canvas.style.height = viewport.height + 'px';

				// Scale context for high DPI displays
				context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

				// Render page
				await page.render({
					canvasContext: context,
					viewport: viewport,
				}).promise;

				this.currentPage = pageNum;
			} catch (err) {
				console.error('Error rendering page:', err);
			} finally {
				this.rendering = false;
			}
		},

		async zoomIn() {
			if (this.scale < 3 && !this.rendering) {
				this.scale = Math.round((this.scale + 0.25) * 100) / 100;
				await this.renderPage(this.currentPage);
			}
		},

		async zoomOut() {
			if (this.scale > 0.5 && !this.rendering) {
				this.scale = Math.round((this.scale - 0.25) * 100) / 100;
				await this.renderPage(this.currentPage);
			}
		},

		async resetZoom() {
			if (this.scale !== 1.0 && !this.rendering) {
				this.scale = 1.0;
				await this.renderPage(this.currentPage);
			}
		},

		handleWheel(event) {
			// Zoom with Ctrl/Cmd + scroll
			if (event.ctrlKey || event.metaKey) {
				event.preventDefault();
				if (event.deltaY < 0) {
					this.zoomIn();
				} else {
					this.zoomOut();
				}
			}
		},

		async goToPage(pageNum) {
			if (pageNum >= 1 && pageNum <= this.totalPages && !this.rendering) {
				await this.renderPage(pageNum);
				if (this.$refs.pdfContainer) {
					this.$refs.pdfContainer.scrollTop = 0;
				}
			}
		},

		async handleDownload() {
			if (!this.base64) return;

			// Turn spinner ON
			this.downloading = true;

			try {
				// We add a tiny delay so the browser has time to render the spinner
				// before the heavy Base64 conversion freezes the UI momentarily.
				await new Promise(resolve => setTimeout(resolve, 100));

				const base64Fetch = await fetch(this.base64);
				const blob = await base64Fetch.blob();
				const FileSaver = require('file-saver');
				FileSaver.saveAs(blob, this.original_file_name);
			} catch (e) {
				console.error('Download failed:', e);
			} finally {
				// Turn spinner OFF
				this.downloading = false;
			}
		},
	},
};
</script>

<style scoped>
.pdf-container {
	display: flex;
	justify-content: center;
	align-items: flex-start;
	padding: 20px;
	height: 600px;
	overflow: auto;
	background: #525659;
	position: relative;
}

.pdf-canvas {
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
	background: white;
}
</style>
